// Custom renderers passed to ReactMarkdown — applies Tailwind styles to each element type so
// AI markdown output is styled. Defined outside the component so the reference is stable and
// ReactMarkdown doesn't remount renderers on every render.

// Matches a lesson href for the `a` renderer — used to detect lesson links in markdown output.
export const LESSON_HREF_RE = /\\?\/lesson\\?\//;

// Matches lesson keys embedded anywhere in markdown — used to extract keys for "Create Custom Path".
export const LESSON_LINK_RE = /\\?\/lesson\\?\/([^\s)"'<>#?&\\]+)/g;

export const CUSTOM_PATH_DRAFT_PREFIX = "sj_ai_custom_path_draft_";

// eslint-disable-next-line react-refresh/only-export-components
export function extractLessonKeys(markdown: string): string[] {
  const keys: string[] = [];
  LESSON_LINK_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = LESSON_LINK_RE.exec(markdown)) !== null) {
    if (!keys.includes(match[1]!)) keys.push(match[1]!);
  }
  return keys;
}

/**
 * Creates a ReactMarkdown `components` map with Atlas-styled renderers.
 *
 * @param renderLessonLink - Optional renderer for lesson links detected by
 *   LESSON_HREF_RE. Each app supplies its own implementation:
 *   - learn app: client-side <Link> navigation
 *   - sj-admin: copy-to-clipboard button
 *   - admin: plain anchor (lesson links open in same tab)
 *   Falls back to a plain external <a> when not provided.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function createMessageComponents(
  renderLessonLink?: (href: string, children: React.ReactNode) => React.ReactNode
) {
  return {
    p: ({ children }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <p className="mb-2 last:mb-0">{children}</p>
    ),
    ul: ({ children }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>
    ),
    ol: ({ children }: React.HTMLAttributes<HTMLOListElement>) => (
      <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>
    ),
    li: ({ children }: React.HTMLAttributes<HTMLLIElement>) => (
      <li className="leading-relaxed">{children}</li>
    ),
    strong: ({ children }: React.HTMLAttributes<HTMLElement>) => (
      <strong className="font-semibold">{children}</strong>
    ),
    em: ({ children }: React.HTMLAttributes<HTMLElement>) => <em className="italic">{children}</em>,
    code: ({ children }: React.HTMLAttributes<HTMLElement>) => (
      <code className="bg-surface-200 text-surface-900 rounded px-1 py-0.5 font-mono text-xs">
        {children}
      </code>
    ),
    pre: ({ children }: React.HTMLAttributes<HTMLPreElement>) => (
      <pre className="bg-surface-200 text-surface-900 rounded p-3 overflow-x-auto font-mono text-xs mb-2">
        {children}
      </pre>
    ),
    h1: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h1 className="font-bold text-base mb-2">{children}</h1>
    ),
    h2: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h2 className="font-bold text-sm mb-2">{children}</h2>
    ),
    h3: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h3 className="font-semibold text-sm mb-1">{children}</h3>
    ),
    hr: () => <hr className="border-surface-300 my-3" />,
    table: ({ children }: React.HTMLAttributes<HTMLTableElement>) => (
      <div className="overflow-x-auto mb-2">
        <table className="w-full text-xs border-collapse">{children}</table>
      </div>
    ),
    thead: ({ children }: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <thead className="bg-surface-200 text-surface-900">{children}</thead>
    ),
    tbody: ({ children }: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <tbody>{children}</tbody>
    ),
    tr: ({ children }: React.HTMLAttributes<HTMLTableRowElement>) => (
      <tr className="border-b border-surface-200 last:border-0">{children}</tr>
    ),
    th: ({ children }: React.HTMLAttributes<HTMLTableCellElement>) => (
      <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">{children}</th>
    ),
    td: ({ children }: React.HTMLAttributes<HTMLTableCellElement>) => (
      <td className="px-3 py-2">{children}</td>
    ),
    a: ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
      if (href && LESSON_HREF_RE.test(href) && renderLessonLink) {
        return <>{renderLessonLink(href, children)}</>;
      }
      return (
        <a
          href={href}
          className="text-primary underline hover:opacity-80"
          target="_blank"
          rel="noreferrer noopener"
        >
          {children}
        </a>
      );
    },
  };
}
